import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { Tooltip } from "@mantine/core";
import { GoQuestion } from "react-icons/go";
import { getAbbreviatedDay, formatedTime } from "../../utils/general";
import "./charts.css";

type dataSetType = {
  occupied: number;
  unoccupied: number;
  floor: string;
  oos: number;
};
type propType = {
  dataSet: dataSetType[];
  small?:boolean
};

const LineChart = ({ dataSet, small }: propType) => {
  const barChartConatiner = useRef(null);
  const [data, setData] = useState([]);
  const status = [
    { label: "Occupied", color: "#F73B3B" },
    { label: "Unoccupied", color: "#008000" },
    { label: "OOS", color: "#F59E0B" },
  ];

  useEffect(() => {
    setData(dataSet);
  }, [dataSet]);
  const [svgWidth, setSvgWidth] = useState<number | null>(null);

  useEffect(() => {
    const svg = d3.select(barChartConatiner.current);
    const getWidth = () => {
      setSvgWidth(svg.node().clientWidth);
    };
    window.addEventListener("resize", getWidth);

    return () => {
      window.removeEventListener("resize", getWidth);
    };
  }, []);

  useEffect(() => {
    const svg = d3.select(barChartConatiner.current);
    const height = 500;
    const margin = { top: 30, bottom: 70, left: 50, right: 50 };

    const width = svgWidth || svg.node().clientWidth;
    const w = width - margin.left - margin.right;
    const h = height - margin.top - margin.bottom;

    svg.selectAll("*").remove();
    d3.selectAll(".tooltip").remove();

    svg.attr("width", "100%").attr("height", h + margin.top + margin.bottom);
    const BARNUM = 3;
    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const xScale = d3
      .scaleBand()
      .domain(dataSet.map((d) => d.floor))
      .range([0, w])
      .padding(0.4);

    const vals = dataSet.flatMap((data) => [
      data.occupied,
      data.oos,
      data.unoccupied,
    ]);

    const yScale = d3
      .scaleLinear()
      .domain(d3.extent(vals))
      .nice()
      .range([h, 0]);

    g.append("g")
      .attr("transform", `translate(0, ${h})`)
      .call(d3.axisBottom(xScale).tickPadding(20).tickSize(6).tickSizeOuter(0))
      .selectAll(".tick text")
      .style("font-size", small ? "11px" : null) 
      .call(wrap, xScale.bandwidth());

      const yAxisGroup = g.append("g")
      .attr("class", "y-axis")
      .call(
        d3
          .axisLeft(yScale)
          .tickSize(-width)
          .tickFormat(null)
          .tickPadding(20)
          .tickSizeOuter(0)
      );

    // Apply conditional font size for y-axis labels
    yAxisGroup.selectAll(".tick text")
      .style("font-size", small ? "12px" : undefined);

    const space = 8;

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("background-color", "rgba(0, 0, 0, 0.8)")
      .style("color", "#fff")
      .style("padding", "5px")
      .style("maxWidth", "250px")
      .style("border-radius", "3px")
      .style("visibility", "hidden");

    //occcupied bar

    g.selectAll(".bars")
      .data(data)
      .join("rect")
      .attr("height", (d) => h - yScale(d.occupied))
      .attr("class", "bar")
      .attr("x", (d) => xScale(d.floor) - space)
      .attr("y", (d) => yScale(d.occupied))
      .attr("rx", 6)
      .attr("width", xScale.bandwidth() / BARNUM)
      .attr("fill", "#F73B3B")
      .on("mouseover", function (event, d) {
        tooltip.style(
          "visibility",
          "visible"
        ).html(`<span style="font-weight:bold; font-size:16px  ">
               ${getAbbreviatedDay(new Date())} at ${formatedTime(new Date())}h
          </span><br/>${
            d.occupied === 1
              ? `${d.occupied} occupied desk`
              : `${d.occupied} occupied desks`
          }  `);
      })
      .on("mousemove", function (event) {
        // Position tooltip relative to mouse pointer
        tooltip
          .style("top", event.pageY - 10 + "px")
          .style("left", event.pageX + 10 + "px");
      })
      .on("mouseout", function () {
        // Hide tooltip
        tooltip.style("visibility", "hidden");
      });

    // unoccupied

    g.selectAll(".bars")
      .data(data)
      .join("rect")
      .attr("height", (d) => h - yScale(d.unoccupied))
      .attr("class", "bar")
      .attr("x", (d) => xScale(d.floor) + xScale.bandwidth() / BARNUM)
      .attr("y", (d) => yScale(d.unoccupied))
      .attr("rx", 6)
      .attr("width", (d) => xScale.bandwidth() / BARNUM)
      .attr("fill", "#008000")
      .on("mouseover", function (event, d) {
        tooltip.style(
          "visibility",
          "visible"
        ).html(`<span style="font-weight:bold; font-size:16px  ">
        ${getAbbreviatedDay(new Date())} at ${formatedTime(new Date())}h
   </span><br/>${
     d.unoccupied === 1
       ? `${d.unoccupied} unoccupied desk`
       : `${d.unoccupied} unoccupied desks`
   }  `);
      })
      .on("mousemove", function (event) {
        // Position tooltip relative to mouse pointer
        tooltip
          .style("top", event.pageY - 10 + "px")
          .style("left", event.pageX + 10 + "px");
      })
      .on("mouseout", function () {
        // Hide tooltip
        tooltip.style("visibility", "hidden");
      });
    //OOS
    g.selectAll(".bars")
      .data(data)
      .join("rect")
      .attr("height", (d) => h - yScale(d.oos))
      .attr("class", "bar")
      .attr(
        "x",
        (d) => xScale(d.floor) + (2 * xScale.bandwidth()) / BARNUM + space
      )
      .attr("y", (d) => yScale(d.oos))
      .attr("rx", 6)
      .attr("width", xScale.bandwidth() / BARNUM)
      .attr("fill", "#F59E0B")
      .on("mouseover", function (event, d) {
        tooltip.style(
          "visibility",
          "visible"
        ).html(`<span style="font-weight:bold; font-size:16px  ">
        ${getAbbreviatedDay(new Date())} at ${formatedTime(new Date())}h
   </span><br/>${
     d.oos === 1
       ? `${d.oos} out of service desk`
       : `${d.oos} out of service desks`
   }  `);
      })
      .on("mousemove", function (event) {
        // Position tooltip relative to mouse pointer
        tooltip
          .style("top", event.pageY - 10 + "px")
          .style("left", event.pageX + 10 + "px");
      })
      .on("mouseout", function () {
        // Hide tooltip
        tooltip.style("visibility", "hidden");
      });

    function wrap(text, width) {
      text.each(function () {
        var text = d3.select(this),
          words = text.text().split(/\s+/).reverse(),
          updatedWords = words.at(-1) ? words : words.slice(0, -1),
          word,
          line = [],
          lineNumber = 0,
          lineHeight = 1.1, // ems
          y = text.attr("y"),
          dy = parseFloat(text.attr("dy")),
          tspan = text
            .text(null)
            .append("tspan")
            .attr("x", 0)
            .attr("y", y)
            .attr("dy", dy + "em");

        while ((word = updatedWords.pop())) {
          line.push(word);
          tspan.text(line.join(" "));
          if (tspan.node().getComputedTextLength() > width) {
            line.pop();
            tspan.text(line.join(" "));
            line = [word];
            tspan = text
              .append("tspan")
              .attr("x", 0)
              .attr("y", y)
              .attr("dy", ++lineNumber * lineHeight + dy + "em")
              .text(word);
          }
        }
      });
    }
  }, [data, svgWidth]);

  return (
    <div className="w-full">
      <div className="flex sm:items-center justify-between flex-col  sm:flex-row mt-4">
       {!small && <p className="font-bold text-lg m-0">Desks overview per floor</p>}
        <div className={`flex  gap-2  ${small ? "justify-center w-full": "justify-end"} my-2 sm:my-0`}>
          {status.map((d, index) => {
            return (
              <div
                key={d.label}
                className={`flex gap-2 justify-center items-center text-sm rounded-[100px] border py-1 px-2 border-solid border-grayText4`}
              >
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ background: d.color }}
                />
                <p>{d.label}</p>
              </div>
            );
          })}
        </div>
      </div>
      <svg ref={barChartConatiner} />
    </div>
  );
};

export default LineChart;
